import { Button, Input, Skeleton } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Search } from "lucide-react";
import * as React from "react";

export interface FilterPillOption {
  value: string;
  label: string;
  count?: number;
}

export interface AdminDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  emptyState?: React.ReactNode;
  searchColumn?: string;
  searchPlaceholder?: string;
  pageSize?: number;
  toolbarRight?: React.ReactNode;
  // Optional filter pill row above the table (Stripe-style "All / Active /
  // Paused" segmented control). Caller owns selected state.
  filters?: {
    options: FilterPillOption[];
    value: string;
    onChange: (value: string) => void;
  };
  onRowClick?: (row: TData) => void;
  className?: string;
  /** Sticky table header background. Defaults to surface (slate-50). */
  stickyHeader?: boolean;
}

/**
 * Admin DataTable — Stripe-Dashboard spec.
 *
 * Differences from libs/ui DataTable:
 * - Renders its own <table> (not the shared TableRow which has different
 *   borders) so we can dial spacing without fighting cascading classes.
 * - 11px uppercase tracked header on a translucent surface background.
 * - Row dividers use the new `border` token, hover uses `bg-surface`.
 * - Inline filter pills above the toolbar.
 * - Mono / tabular conventions are caller-supplied via column meta classes.
 */
export function AdminDataTable<TData, TValue>({
  columns,
  data,
  loading = false,
  emptyState = "No results.",
  searchColumn,
  searchPlaceholder = "Search…",
  pageSize = 25,
  toolbarRight,
  filters,
  onRowClick,
  className,
  stickyHeader = true,
}: AdminDataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = React.useState("");

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  React.useEffect(() => {
    if (!searchColumn) return;
    const column = table.getColumn(searchColumn);
    if (!column) return;
    column.setFilterValue(globalFilter || undefined);
  }, [globalFilter, searchColumn, table]);

  const rowCount = table.getRowModel().rows.length;
  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {filters && (
        <div className="flex items-center gap-1 rounded-md border border-border bg-card p-1">
          {filters.options.map((opt) => {
            const isActive = opt.value === filters.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => filters.onChange(opt.value)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-[12px] font-medium transition-colors duration-120 ease-out",
                  isActive
                    ? "bg-surface text-foreground"
                    : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
                )}
              >
                {opt.label}
                {opt.count !== undefined && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-px text-[10px] font-semibold tabular-nums",
                      isActive ? "bg-card text-muted-foreground" : "bg-surface text-subtle",
                    )}
                  >
                    {opt.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {(searchColumn || toolbarRight) && (
        <div className="flex items-center gap-2">
          {searchColumn && (
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-subtle" />
              <Input
                value={globalFilter}
                onChange={(event) => setGlobalFilter(event.target.value)}
                placeholder={searchPlaceholder}
                className="h-8 border-border bg-card pl-8 text-[13px]"
              />
            </div>
          )}
          {toolbarRight && <div className="ml-auto flex items-center gap-2">{toolbarRight}</div>}
        </div>
      )}

      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="relative w-full overflow-auto">
          <table className="w-full text-[13px]">
            <thead
              className={cn(
                stickyHeader && "sticky top-0 z-10",
                "border-b border-border bg-surface/80 backdrop-blur",
              )}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        className={cn(
                          "h-9 px-4 text-left align-middle text-[11px] font-semibold uppercase tracking-[0.06em] text-subtle",
                          canSort && "cursor-pointer select-none hover:text-foreground",
                        )}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="inline-flex items-center gap-1">
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <span className="text-subtle/70">
                                {sortDir === "asc" ? (
                                  <ArrowUp className="size-3" />
                                ) : sortDir === "desc" ? (
                                  <ArrowDown className="size-3" />
                                ) : null}
                              </span>
                            )}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>

            <tbody className="divide-y divide-border/60">
              {loading ? (
                Array.from({ length: 5 }).map((_, rowIndex) => (
                  <tr key={`skeleton-${rowIndex}`}>
                    {columns.map((_col, colIndex) => (
                      <td key={`skeleton-cell-${colIndex}`} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : rowCount > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={cn(
                      "transition-colors duration-120 ease-out",
                      onRowClick && "cursor-pointer",
                      "hover:bg-surface",
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 align-middle text-foreground">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="h-24 px-4 text-center text-[13px] text-muted-foreground"
                  >
                    {emptyState}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-[12px] text-muted-foreground">
          <p className="tabular-nums">
            Page {pageIndex + 1} of {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-7 border-border bg-card text-[12px]"
            >
              <ChevronLeft className="size-3.5" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-7 border-border bg-card text-[12px]"
            >
              Next
              <ChevronRight className="size-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
